import { Barcode, Package } from 'lucide-react';
import { useEffect, useLayoutEffect, useRef, useState, type RefObject } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Button,
  EmptyState,
  ErrorState,
  InlineAlert,
  Label,
  SearchInput,
  SelectMenu,
  Spinner,
  Switch,
} from '@/shared/ui';

import { useOfflineProductSearchQuery } from '../api/offlineOrdersApi';
import type { BarcodeScanOutcome } from '../hooks/useOfflineSaleState';
import {
  OFFLINE_MAX_QUANTITY,
  type OfflineProduct,
  type OfflineProductSortBy,
  type OfflineSortOrder,
} from '../types';

import { OfflineProductCard } from './OfflineProductCard';

export interface OfflineProductSearchProps {
  storeId: string;
  disabled?: boolean;
  /** True while a cart mutation is in flight. Does not disable the barcode field. */
  busy?: boolean;
  barcodeInputRef?: RefObject<HTMLInputElement>;
  onAdd: (productId: string) => Promise<void> | void;
  onScan: (raw: string) => Promise<BarcodeScanOutcome>;
}

export function OfflineProductSearch({
  storeId,
  disabled = false,
  busy = false,
  barcodeInputRef,
  onAdd,
  onScan,
}: OfflineProductSearchProps) {
  const { t } = useTranslation('offlineOrders');
  const [barcode, setBarcode] = useState('');
  const [scanning, setScanning] = useState(false);
  const [scanHits, setScanHits] = useState<OfflineProduct[] | null>(null);
  const [scanNotice, setScanNotice] = useState<'none' | 'max' | null>(null);
  const [addedName, setAddedName] = useState<string | null>(null);
  const [scanError, setScanError] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [page, setPage] = useState(1);
  const [inStockOnly, setInStockOnly] = useState(true);
  const [onOfferOnly, setOnOfferOnly] = useState(false);
  const [sortBy, setSortBy] = useState<OfflineProductSortBy>('relevance');
  const [sortOrder, setSortOrder] = useState<OfflineSortOrder>('asc');
  const onScanRef = useRef(onScan);
  const scanQueueRef = useRef<string[]>([]);
  const scanBusyRef = useRef(false);
  const refocusAfterScanRef = useRef(false);

  onScanRef.current = onScan;

  const applyScanOutcome = (outcome: BarcodeScanOutcome) => {
    if (outcome.kind === 'none') {
      setScanHits(null);
      setScanNotice('none');
    } else if (outcome.kind === 'many') {
      setScanHits(outcome.items);
    } else if (outcome.kind === 'max') {
      setScanHits(null);
      setScanNotice('max');
    } else if (outcome.kind === 'error') {
      setScanHits(null);
      setScanError(outcome.message);
    } else if (outcome.kind === 'added') {
      setScanHits(null);
      setAddedName(outcome.name);
    } else {
      setScanHits(null);
    }
  };

  const flushScanQueue = async () => {
    if (scanBusyRef.current) return;
    scanBusyRef.current = true;
    setScanning(true);
    try {
      while (scanQueueRef.current.length > 0) {
        const next = scanQueueRef.current.shift();
        if (!next) continue;
        setScanNotice(null);
        setScanError(null);
        setAddedName(null);
        applyScanOutcome(await onScanRef.current(next));
      }
    } finally {
      scanBusyRef.current = false;
      setScanning(false);
      refocusAfterScanRef.current = true;
      barcodeInputRef?.current?.focus();
    }
    if (scanQueueRef.current.length > 0) {
      void flushScanQueue();
    }
  };

  const submitScan = (raw: string) => {
    const code = raw.trim();
    if (!code || disabled) return;
    setBarcode('');
    if (barcodeInputRef?.current) barcodeInputRef.current.value = '';
    scanQueueRef.current.push(code);
    void flushScanQueue();
  };

  useEffect(() => {
    setPage(1);
    setScanHits(null);
    setScanNotice(null);
    setScanError(null);
    setAddedName(null);
  }, [storeId]);

  useLayoutEffect(() => {
    if (scanning || !refocusAfterScanRef.current) return;
    refocusAfterScanRef.current = false;
    barcodeInputRef?.current?.focus();
  }, [barcodeInputRef, scanning]);

  const search = useOfflineProductSearchQuery({
    storeId,
    query: debouncedQuery.trim() || undefined,
    page,
    limit: 20,
    inStockOnly,
    onOfferOnly,
    sortBy,
    sortOrder,
  });

  const pageCount = search.data?.pageCount ?? 1;

  return (
    <section className="flex flex-col gap-4">
      <div className="rounded-lg border border-border bg-surface p-4">
        <div className="flex items-center gap-2">
          <Barcode aria-hidden="true" className="h-5 w-5 text-primary" />
          <h2 className="text-base font-semibold text-fg">{t('search.barcodeLabel')}</h2>
        </div>
        <p className="mt-1 text-sm text-fg-muted">{t('search.barcodeHelp')}</p>
        <div className="mt-3 flex flex-col gap-2 sm:flex-row">
          <div className="flex-1">
            <Label htmlFor="offline-barcode" className="sr-only">
              {t('search.barcodeLabel')}
            </Label>
            {/* Never `disabled` here: a disabled input blurs and blocks the next scan. */}
            <input
              ref={barcodeInputRef}
              id="offline-barcode"
              value={barcode}
              autoComplete="off"
              readOnly={disabled}
              aria-busy={scanning}
              placeholder={t('search.barcodePlaceholder')}
              onChange={(event) => setBarcode(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === 'Tab') {
                  event.preventDefault();
                  submitScan(event.currentTarget.value);
                }
              }}
              className="h-10 w-full rounded-sm border border-border-strong bg-surface px-3 text-base text-fg placeholder:text-fg-subtle focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            />
          </div>
          <Button
            type="button"
            disabled={disabled || barcode.trim().length === 0}
            onMouseDown={(event) => {
              event.preventDefault();
            }}
            onClick={() => submitScan(barcode)}
          >
            {scanning ? t('search.scanning') : t('search.scan')}
          </Button>
        </div>
        <div className="mt-3" aria-live="polite">
          {scanNotice === 'none' ? <InlineAlert>{t('search.noBarcode')}</InlineAlert> : null}
          {scanNotice === 'max' ? (
            <InlineAlert>{t('cart.maxQuantity', { max: OFFLINE_MAX_QUANTITY })}</InlineAlert>
          ) : null}
          {scanError ? <InlineAlert>{scanError}</InlineAlert> : null}
          {addedName ? (
            <p className="text-sm text-success-strong" role="status">
              {t('search.added', { name: addedName })}
            </p>
          ) : null}
        </div>
      </div>

      {scanHits && scanHits.length > 1 ? (
        <div className="flex flex-col gap-2">
          <h2 className="text-sm font-semibold text-fg">{t('search.scanResults')}</h2>
          <ul className="flex flex-col gap-2">
            {scanHits.map((product, index) => (
              <li key={product.productId}>
                <OfflineProductCard
                  product={product}
                  exactMatch={index === 0}
                  disabled={disabled || busy}
                  onAdd={(productId) => void onAdd(productId)}
                />
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <div className="flex flex-col gap-3">
        <SearchInput
          id="offline-product-search"
          label={t('search.nameLabel')}
          placeholder={t('search.namePlaceholder')}
          value={query}
          onValueChange={(next) => {
            setQuery(next);
            setPage(1);
          }}
          onDebouncedChange={(next) => {
            setDebouncedQuery(next);
            setPage(1);
          }}
        />
        <div className="flex flex-wrap items-center gap-4">
          <label className="flex items-center gap-2 text-sm text-fg">
            <Switch
              checked={inStockOnly}
              onCheckedChange={(checked) => {
                setInStockOnly(checked);
                setPage(1);
              }}
              aria-label={t('search.inStockOnly')}
            />
            {t('search.inStockOnly')}
          </label>
          <label className="flex items-center gap-2 text-sm text-fg">
            <Switch
              checked={onOfferOnly}
              onCheckedChange={(checked) => {
                setOnOfferOnly(checked);
                setPage(1);
              }}
              aria-label={t('search.onOfferOnly')}
            />
            {t('search.onOfferOnly')}
          </label>
          <div className="min-w-36">
            <SelectMenu
              value={sortBy}
              onChange={(value) => {
                setSortBy(value as OfflineProductSortBy);
                setPage(1);
              }}
              placeholder={t('search.sortBy')}
              emptyLabel={t('search.sort.relevance')}
              options={[
                { value: 'relevance', label: t('search.sort.relevance') },
                { value: 'name', label: t('search.sort.name') },
                { value: 'price', label: t('search.sort.price') },
              ]}
            />
          </div>
          <div className="min-w-36">
            <SelectMenu
              value={sortOrder}
              onChange={(value) => {
                setSortOrder(value as OfflineSortOrder);
                setPage(1);
              }}
              placeholder={t('search.sortOrder')}
              emptyLabel={t('search.sort.asc')}
              options={[
                { value: 'asc', label: t('search.sort.asc') },
                { value: 'desc', label: t('search.sort.desc') },
              ]}
            />
          </div>
        </div>
      </div>

      {search.isLoading ? (
        <div className="flex justify-center py-10">
          <Spinner label={t('search.scanning')} />
        </div>
      ) : search.isError ? (
        <ErrorState onRetry={() => void search.refetch()} />
      ) : (search.data?.items.length ?? 0) === 0 ? (
        <EmptyState
          icon={Package}
          title={t('search.emptyTitle')}
          description={
            debouncedQuery.trim() ? t('search.emptyDescription') : t('search.browseEmpty')
          }
        />
      ) : (
        <ul className="flex flex-col gap-2">
          {search.data?.items.map((product) => (
            <li key={product.productId}>
              <OfflineProductCard
                product={product}
                disabled={disabled || busy}
                onAdd={(productId) => void onAdd(productId)}
              />
            </li>
          ))}
        </ul>
      )}

      {pageCount > 1 ? (
        <div className="flex items-center justify-between gap-3">
          <Button
            type="button"
            variant="secondary"
            disabled={page <= 1 || search.isFetching}
            onClick={() => setPage((current) => Math.max(1, current - 1))}
          >
            {t('search.previous')}
          </Button>
          <span className="text-sm text-fg-muted">
            {t('search.page', { page, pages: pageCount })}
          </span>
          <Button
            type="button"
            variant="secondary"
            disabled={page >= pageCount || search.isFetching}
            onClick={() => setPage((current) => current + 1)}
          >
            {t('search.next')}
          </Button>
        </div>
      ) : null}
    </section>
  );
}
